import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '../utils/api';
import { ApiError, ApiResponse } from '../types/api';

export type NotificationChannel = 'email' | 'sms';

export interface NotificationChannels {
    email: boolean;
    sms: boolean;
}

export interface NotificationSubscriptionEntry {
    channels: NotificationChannels;
    /** ISO timestamp while a manual snooze is active, else null/absent. */
    snoozed_until?: string | null;
}

/** One row of the project's recipients list: a user in the organisation
 *  with their subscriptions to this project (absent kind = none). */
export interface ProjectNotificationRecipient {
    id: number;
    username: string;
    email: string;
    /** E.164 or empty — SMS cannot be switched on without one. */
    phone_number: string;
    is_staff: boolean;
    subscriptions: Record<string, NotificationSubscriptionEntry>;
}

export interface ProjectNotificationSettings {
    /** The current user's subscriptions, keyed by kind; absent kind = none. */
    subscriptions: Record<string, NotificationSubscriptionEntry>;
    /** Every active user in the organisation, sorted by username, with
     *  their subscriptions to this project — who actually gets alerted. */
    users: ProjectNotificationRecipient[];
    /** Whether the caller (a tenant admin) may edit rows other than their
     *  own, and the project-level radius. Everyone can always edit their own
     *  row. */
    can_manage: boolean;
    /** The project's alert radius, inherited by every subscriber. */
    radius_km: number;
    /** Kind usable at all (data source exists AND tenant opted in). */
    available_kinds: Record<string, boolean>;
    /** Whether a connector exists, regardless of tenant opt-in — decides
     *  which "unavailable" explanation applies. */
    configured_kinds: Record<string, boolean>;
    max_radius_km: number;
}

/** Per-kind patch; channels merge per key server-side. snooze_hours is
 *  write-only: >0 snoozes for that long, 0 ends the snooze; the response
 *  reports the resulting snoozed_until. */
export type NotificationSubscriptionUpdate = Record<
    string,
    {
        channels?: Partial<NotificationChannels>;
        snooze_hours?: number;
    }
>;

/** One PATCH body: a per-kind update, optionally for another user in the
 *  organisation (admins only; omitted = the caller's own row), and/or the
 *  project-level alert radius (admins only). */
export interface ProjectNotificationPatch {
    subscriptions?: NotificationSubscriptionUpdate;
    user_id?: number;
    radius_km?: number;
}

const DEFAULT_ENTRY: NotificationSubscriptionEntry = {
    channels: { email: false, sms: false },
};

/** Optimistic per-kind merge, mirroring the server's rules: channels merge
 *  per key, snooze is approximated from the client clock until the settle
 *  refetch brings server truth. */
const mergeSubscriptions = (
    current: Record<string, NotificationSubscriptionEntry>,
    updates: NotificationSubscriptionUpdate
): Record<string, NotificationSubscriptionEntry> => {
    const merged = { ...current };
    for (const [kind, entry] of Object.entries(updates)) {
        const existing = merged[kind] ?? DEFAULT_ENTRY;
        merged[kind] = {
            channels: { ...existing.channels, ...(entry.channels ?? {}) },
            snoozed_until:
                entry.snooze_hours === undefined
                    ? existing.snoozed_until
                    : entry.snooze_hours > 0
                      ? new Date(
                            Date.now() + entry.snooze_hours * 3_600_000
                        ).toISOString()
                      : null,
        };
    }
    return merged;
};

export const useProjectNotificationSubscriptions = (
    projectId: string | number | undefined
) => {
    const idKey = projectId !== undefined ? String(projectId) : undefined;
    return useQuery({
        queryKey: ['project', idKey, 'notification-subscriptions'],
        queryFn: () =>
            api.request<ApiResponse<ProjectNotificationSettings>>(
                `/api/projects/${idKey}/notification-subscriptions`
            ),
        enabled: !!idKey,
        refetchOnWindowFocus: false,
    });
};

/**
 * Per-kind merge PATCH for the notification center. Same optimistic +
 * burst-guard shape as the other optimistic toggles: switches must move
 * on click, and only the last mutation of a burst refetches so an earlier
 * response can't snap a later toggle back.
 *
 * `selfUserId` lets the optimistic update keep the caller's own cards and
 * their row in the recipients list in step — the two views of one set of
 * rows must never disagree, even for a moment.
 */
export const useUpdateNotificationSubscription = (
    projectId: string | number | undefined,
    selfUserId?: number
) => {
    const queryClient = useQueryClient();
    const idKey = projectId !== undefined ? String(projectId) : undefined;
    const queryKey = ['project', idKey, 'notification-subscriptions'];
    const mutationKey = ['project-notification-subscriptions', idKey];

    return useMutation<
        ApiResponse<ProjectNotificationSettings>,
        ApiError,
        ProjectNotificationPatch,
        { previous?: ApiResponse<ProjectNotificationSettings> }
    >({
        mutationKey,
        mutationFn: (body) =>
            api.patch<ApiResponse<ProjectNotificationSettings>>(
                `/api/projects/${idKey}/notification-subscriptions`,
                body
            ),
        onMutate: async ({ subscriptions = {}, user_id, radius_km }) => {
            if (!idKey) return {};
            await queryClient.cancelQueries({ queryKey });
            const previous =
                queryClient.getQueryData<ApiResponse<ProjectNotificationSettings>>(
                    queryKey
                );
            if (previous?.data) {
                const targetId = user_id ?? selfUserId;
                const isSelf = user_id === undefined || user_id === selfUserId;
                queryClient.setQueryData<ApiResponse<ProjectNotificationSettings>>(
                    queryKey,
                    {
                        ...previous,
                        data: {
                            ...previous.data,
                            radius_km: radius_km ?? previous.data.radius_km,
                            subscriptions: isSelf
                                ? mergeSubscriptions(
                                      previous.data.subscriptions,
                                      subscriptions
                                  )
                                : previous.data.subscriptions,
                            users: (previous.data.users ?? []).map((u) =>
                                u.id === targetId
                                    ? {
                                          ...u,
                                          subscriptions: mergeSubscriptions(
                                              u.subscriptions,
                                              subscriptions
                                          ),
                                      }
                                    : u
                            ),
                        },
                    }
                );
            }
            return { previous };
        },
        onError: (_error, _variables, context) => {
            if (idKey && context?.previous) {
                queryClient.setQueryData(queryKey, context.previous);
            }
        },
        onSettled: (_data, _error, { user_id }) => {
            if (queryClient.isMutating({ mutationKey }) > 1) return;
            queryClient.invalidateQueries({ queryKey });
            // My Account shows every project's subscriptions in one table, so
            // a change made here has to reach it too — and the admin user
            // page shows the same rows for whoever was edited.
            queryClient.invalidateQueries({ queryKey: ['my-subscriptions'] });
            if (user_id !== undefined) {
                queryClient.invalidateQueries({
                    queryKey: ['admin-user-subscriptions', user_id],
                });
            }
        },
    });
};
